import React, { useEffect, useState } from "react";
import {
  Badge,
  Button,
  Code,
  Group,
  List,
  Modal,
  NumberInput,
  Select,
  Stack,
  Switch,
  Tabs,
  Text,
  Textarea,
  TextInput,
  ThemeIcon,
  Title,
  Alert,
} from "@mantine/core";
import {
  IconAdjustments,
  IconBook,
  IconCircleCheck,
  IconExclamationMark,
  IconRoute,
  IconSettings,
} from "@tabler/icons";
import { showNotification } from "@mantine/notifications";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { Page, FormSectionCard } from "../../layout/page";
import PageListHeader from "../../components/pageListHeader";
import { useServerApi } from "../../hooks/userServerApi";
import { useTabNavigate } from "../../hooks/useTabNavigate";
import {
  DEFAULT_PATH_TEMPLATES,
  DEFAULT_ENTITY_TYPE_ENABLED,
  DEFAULT_WORKER,
  ENTITY_BACKUP_TYPE_META,
  normalizeFileBackupSettings,
  parseServiceAccountEmailFromJson,
  getFileBackupApiErrorMessage,
  getFileBackupErrorPresentation,
  serializeFileBackupSettings,
  validateFileBackupSettings,
} from "../../helpers/fileBackupSettingsUtils";

const FileBackupErrorHelpModal = ({ opened, onClose, presentation }) => {
  const { t } = useTranslation();
  if (!presentation) return null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t(presentation.title)}
      size="lg"
      centered
    >
      <Stack spacing="md">
        <Alert color="red" variant="light" title={t("What happened")}>
          <Text size="sm">{t(presentation.summary)}</Text>
        </Alert>

        {(presentation.folderId || presentation.serviceAccountEmail) && (
          <Stack spacing="sm">
            {presentation.folderId && (
              <Stack spacing={4}>
                <Text size="xs" weight={600} color="dimmed">
                  {t("Root folder ID")}
                </Text>
                <Code block sx={{ wordBreak: "break-all" }}>
                  {presentation.folderId}
                </Code>
              </Stack>
            )}
            {presentation.serviceAccountEmail && (
              <Stack spacing={4}>
                <Text size="xs" weight={600} color="dimmed">
                  {t("Share the folder with this email (Editor access)")}
                </Text>
                <Code block sx={{ wordBreak: "break-all" }}>
                  {presentation.serviceAccountEmail}
                </Code>
              </Stack>
            )}
          </Stack>
        )}

        {presentation.steps?.length > 0 && (
          <Stack spacing="xs">
            <Text size="sm" weight={600}>{t("What to do")}</Text>
            <List spacing="xs" size="sm" withPadding>
              {presentation.steps.map((step) => (
                <List.Item key={step}>
                  <Text size="sm">{t(step)}</Text>
                </List.Item>
              ))}
            </List>
          </Stack>
        )}

        {presentation.technical && (
          <Text size="xs" color="dimmed">
            {t("Technical details")}: {presentation.technical}
          </Text>
        )}
      </Stack>
    </Modal>
  );
};

const SETUP_GUIDE_STEPS = [
  {
    title: "Create a Google Cloud service account",
    items: [
      "Sign in to Google Cloud Console (https://console.cloud.google.com).",
      "Create a new project, or select the project you want MBID to use for Drive access.",
      "Open APIs & Services → Library, search for Google Drive API, and click Enable.",
      "Go to APIs & Services → Credentials → Create credentials → Service account.",
      "Enter a service account name (for example MBID File Backup) and finish creating the account.",
      "Open the new service account → Keys → Add key → Create new key → JSON.",
      "Download the JSON key file and keep it secure. You will paste its contents into MBID in Step 4.",
    ],
  },
  {
    title: "Paste credentials in MBID",
    items: [
      "In MBID, go to Settings → Others → File Backup Sync.",
      "Open the Settings tab.",
      "Paste the full service account JSON into the Service account JSON field.",
      "Click Save. The page shows Credentials configured and the service account email from your JSON.",
      "Copy that email — you need it for the next step.",
    ],
  },
  {
    title: "Prepare the Google Drive folder",
    items: [
      "In Google Drive, create a root folder for MBID backups (or use a folder the client already shares with you).",
      "Example folder name: MBID File Backup or Client Name – MBID Uploads.",
      "Open the folder, click Share, and add the service account email shown on the MBID Settings tab.",
      "Grant Editor access so MBID can create folders and upload files.",
      "Copy the folder ID from the browser URL. It is the string after /folders/ in the address bar.",
      "Example URL: https://drive.google.com/drive/folders/1AbCdeFgHiJkLmNoPqRsTuVwXyZ → folder ID is 1AbCdeFgHiJkLmNoPqRsTuVwXyZ.",
    ],
  },
  {
    title: "Configure MBID — folder and test",
    items: [
      "On the File Backup Sync Settings tab, paste the Google Drive root folder ID.",
      "Click Save.",
      "Click Test connection. A success message confirms MBID can access the shared folder.",
    ],
  },
  {
    title: "Enable sync and tune options",
    items: [
      "Turn on Enable file backup sync.",
      "Confirm Provider is Google Drive.",
      "Choose whether Trash files on delete should move removed MBID files to Google Drive trash.",
      "Open the Path templates tab and turn on only the file types you need (e.g. Debit Note only).",
      "Optional: customize folder path templates for each enabled file type.",
      "Optional: open the Advanced settings tab to change Max retries and Batch size for the background worker.",
      "Click Save.",
    ],
  },
  {
    title: "Backfill existing files and verify",
    items: [
      "After saving with sync enabled, click Backfill to enqueue uploads for files already on the MBID server.",
      "Backfill may take time for large archives. The worker runs every few minutes in the background.",
      "Watch the status counters: Pending should decrease and Sync records should increase.",
      "If jobs fail, read the error from the failed count, fix the configuration, then click Retry failed.",
      "Upload a test file to a Debit Note and confirm it appears under the expected folder in Google Drive.",
    ],
  },
  {
    title: "Expected folder structure (Debit Note example)",
    items: [
      "Under your root folder, MBID creates paths like:",
      "Debit Note / 2026 / 202601 / DN26010001 / policy.pdf",
      "Year and month come from the debit note date or DN code (DN2601… → 2026 / 202601).",
      "Other entities use their own templates (Orders, Clients, Uploads fallback, etc.).",
    ],
  },
  {
    title: "Troubleshooting",
    items: [
      "Test connection fails: verify the folder is shared with the exact service account email shown on the page.",
      "Uploads stay pending: confirm Enable file backup sync is on and the processFileBackupJobs cron job is active.",
      "Wrong folder path: check Path templates on the Settings tab and ensure uploads pass entity context (e.g. debit note code).",
      "Credentials lost after save: only paste new JSON when replacing credentials; leaving the field blank keeps the stored key.",
      "Sync is one-way only: changes made directly in Google Drive are not imported back into MBID.",
    ],
  },
];

const FileBackupSetupGuideTab = ({ serviceAccountEmail }) => {
  const { t } = useTranslation();

  return (
    <Stack spacing="lg">
      <FormSectionCard>
        <Stack spacing="sm">
          <Title order={5}>{t("Overview")}</Title>
          <Text size="sm" color="dimmed">
            {t(
              "File Backup Sync mirrors internal staff file attachments to Google Drive one-way. MBID remains the source of truth. Consultant-only file lists are not included."
            )}
          </Text>
          {serviceAccountEmail && (
            <Text size="sm">
              {t("Current service account")}:{" "}
              <Code>{serviceAccountEmail}</Code>
            </Text>
          )}
        </Stack>
      </FormSectionCard>

      {SETUP_GUIDE_STEPS.map((section, sectionIndex) => (
        <FormSectionCard key={section.title}>
          <Group spacing="sm" mb="sm" noWrap align="flex-start">
            <ThemeIcon size="lg" radius="xl" variant="light">
              {sectionIndex + 1}
            </ThemeIcon>
            <Title order={5}>{t(section.title)}</Title>
          </Group>
          <List spacing="xs" size="sm" withPadding>
            {section.items.map((item) => (
              <List.Item key={item}>
                <Text size="sm" color="dimmed">{t(item)}</Text>
              </List.Item>
            ))}
          </List>
        </FormSectionCard>
      ))}
    </Stack>
  );
};

const FileBackupSyncSetting = () => {
  const { t } = useTranslation();
  const [api] = useServerApi();
  const tabNav = useTabNavigate();
  const [activeTab, setActiveTab] = useState("settings");
  const [settingId, setSettingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState("");
  const [jobStatus, setJobStatus] = useState(null);
  const [connectionHelp, setConnectionHelp] = useState(null);
  const [connectionHelpOpen, setConnectionHelpOpen] = useState(false);

  const [formValues, setFormValues] = useState({
    enabled: false,
    provider: "googleDrive",
    googleDrive: {
      rootFolderId: "",
      trashOnDelete: true,
    },
    serviceAccountJson: "",
    pathTemplates: { ...DEFAULT_PATH_TEMPLATES },
    entityTypeEnabled: { ...DEFAULT_ENTITY_TYPE_ENABLED },
    worker: { ...DEFAULT_WORKER },
    credentialsConfigured: false,
    serviceAccountEmail: "",
  });

  const displayedServiceAccountEmail =
    formValues.serviceAccountEmail ||
    parseServiceAccountEmailFromJson(formValues.serviceAccountJson);

  const showFileBackupFailure = (result, { useHelpModal = false } = {}) => {
    const presentation = getFileBackupErrorPresentation(result, api.getErrorMsg);
    if (!presentation) {
      showNotification({
        title: t("Error"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: t("Action failed."),
      });
      return;
    }

    if (useHelpModal) {
      setConnectionHelp(presentation);
      setConnectionHelpOpen(true);
      return;
    }

    showNotification({
      title: t(presentation.title),
      color: "red",
      icon: <IconExclamationMark size={18} />,
      message: t(presentation.summary),
      autoClose: 12000,
    });
  };

  const refreshStatus = async () => {
    try {
      setStatusLoading(true);
      const result = await api.fileBackup.getStatus();
      if (result?.success) {
        setJobStatus(result.data?.status || null);
        if (result.data?.settings) {
          setFormValues((prev) => ({
            ...normalizeFileBackupSettings(result.data.settings),
            serviceAccountJson: "",
          }));
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setStatusLoading(false);
    }
  };

  const fetchSetting = async () => {
    try {
      setLoading(true);
      const result = await api.search({
        apiEntity: "setting",
        pageSize: 1,
      });

      if (!result || _.isEmpty(result.docs)) {
        setSettingId(null);
        await refreshStatus();
        return;
      }

      const setting = result.docs[0];
      setSettingId(setting._id);
      setFormValues({
        ...normalizeFileBackupSettings(setting.fileBackup),
        serviceAccountJson: "",
      });
      await refreshStatus();
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSetting();
  }, []);

  const updateField = (path, value) => {
    setFormValues((prev) => {
      const next = _.cloneDeep(prev);
      _.set(next, path, value);
      return next;
    });
  };

  const handleSave = async () => {
    const validationError = validateFileBackupSettings(formValues);
    if (validationError) {
      showNotification({
        title: t("Save"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: validationError,
      });
      return;
    }

    try {
      setSaving(true);
      const fileBackupPayload = serializeFileBackupSettings({
        formValues,
        credentialsConfigured: formValues.credentialsConfigured,
        serviceAccountEmail: formValues.serviceAccountEmail,
      });
      delete fileBackupPayload.credentialsConfigured;
      delete fileBackupPayload.serviceAccountEmail;

      const result = await api.fileBackup.saveSettings(fileBackupPayload);

      if (result?.success) {
        showNotification({
          title: t("Save"),
          color: "green",
          icon: <IconCircleCheck size={18} />,
          message: t("File backup settings saved."),
        });
        if (result.data) {
          setFormValues({
            ...normalizeFileBackupSettings(result.data),
            serviceAccountJson: "",
          });
          if (!settingId) {
            const settingResult = await api.search({
              apiEntity: "setting",
              pageSize: 1,
            });
            if (settingResult?.docs?.[0]?._id) {
              setSettingId(settingResult.docs[0]._id);
            }
          }
        }
        await refreshStatus();
        return;
      }

      showNotification({
        title: t("Save"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: getFileBackupApiErrorMessage(
          result,
          api.getErrorMsg,
          t("Failed to save settings.")
        ),
      });
      if (result?.error?.fileBackupError) {
        const presentation = getFileBackupErrorPresentation(result, api.getErrorMsg);
        setConnectionHelp(presentation);
        setConnectionHelpOpen(true);
      }
    } catch (error) {
      console.log(error);
      showNotification({
        title: t("Save"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: t("Failed to save settings."),
      });
    } finally {
      setSaving(false);
    }
  };

  const runAction = async (actionName, fn) => {
    try {
      setActionLoading(actionName);
      const result = await fn();
      if (result?.success) {
        const folderName = result.data?.folderName;
        if (actionName === "test") {
          setConnectionHelp(null);
          setConnectionHelpOpen(false);
        }
        showNotification({
          title: t("Success"),
          color: "green",
          icon: <IconCircleCheck size={18} />,
          message:
            folderName
              ? t("Connected to folder: {{name}}", { name: folderName })
              : result.message || t("Completed successfully."),
        });
        await refreshStatus();
      } else {
        showFileBackupFailure(result, {
          useHelpModal: actionName === "test",
        });
      }
    } catch (error) {
      showNotification({
        title: t("Error"),
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: error?.message || t("Action failed."),
      });
    } finally {
      setActionLoading("");
    }
  };

  return (
    <Page>
      <PageListHeader
        title={t("File Backup Sync")}
        actionButtons={[
          {
            label: t("Save"),
            onClick: handleSave,
            loading: saving,
            disabled:
              loading ||
              activeTab === "setupGuide",
          },
          {
            label: t("Close"),
            onClick: () => tabNav.closeActive(),
            variant: "default",
          },
        ]}
      />

      {!loading && (
        <Tabs value={activeTab} onTabChange={setActiveTab}>
          <Tabs.List mb="md">
            <Tabs.Tab value="settings" icon={<IconSettings size={14} />}>
              {t("Settings")}
            </Tabs.Tab>
            <Tabs.Tab value="pathTemplates" icon={<IconRoute size={14} />}>
              {t("Path templates")}
            </Tabs.Tab>
            <Tabs.Tab value="advanced" icon={<IconAdjustments size={14} />}>
              {t("Advanced settings")}
            </Tabs.Tab>
            <Tabs.Tab value="setupGuide" icon={<IconBook size={14} />}>
              {t("Setup guide")}
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="settings">
            <Stack spacing="md">
              <FormSectionCard
                title="Basic settings"
                description="Enable sync, choose the provider, and set the Google Drive destination folder."
              >
                <Stack spacing="md">
                  <Stack spacing="sm">
                    <Switch
                      size="md"
                      label={t("Enable file backup sync")}
                      checked={formValues.enabled}
                      onChange={(event) =>
                        updateField("enabled", event.currentTarget.checked)
                      }
                    />
                    <Switch
                      size="md"
                      label={t("Move to Google Drive trash on delete")}
                      checked={formValues.googleDrive?.trashOnDelete !== false}
                      onChange={(event) =>
                        updateField("googleDrive.trashOnDelete", event.currentTarget.checked)
                      }
                    />
                  </Stack>
                  <Select
                    label={t("Provider")}
                    value={formValues.provider}
                    onChange={(value) => updateField("provider", value || "googleDrive")}
                    data={[
                      { value: "googleDrive", label: "Google Drive" },
                      { value: "s3", label: "AWS S3 (coming soon)", disabled: true },
                      { value: "vps", label: "VPS (coming soon)", disabled: true },
                    ]}
                  />
                  <TextInput
                    label={t("Root folder ID")}
                    description={
                      displayedServiceAccountEmail
                        ? t(
                            "Share this folder with the service account email below, then paste the folder ID from the Drive URL."
                          )
                        : t(
                            "Paste and save credentials below first, then share your folder and paste its ID here."
                          )
                    }
                    placeholder="1AbCdeFgHiJkLmNoPqRsTuVwXyZ"
                    value={formValues.googleDrive?.rootFolderId || ""}
                    onChange={(event) =>
                      updateField("googleDrive.rootFolderId", event.currentTarget.value)
                    }
                  />
                </Stack>
              </FormSectionCard>

              <FormSectionCard
                title="Google service account"
                description="Paste the JSON key once; it is stored securely and not shown again."
              >
                <Stack spacing="sm">
                  <Group spacing="xs">
                    {formValues.credentialsConfigured ? (
                      <Badge color="green" variant="light">
                        {t("Credentials configured")}
                      </Badge>
                    ) : (
                      <Badge color="gray" variant="light">
                        {t("Credentials not configured")}
                      </Badge>
                    )}
                    {displayedServiceAccountEmail && (
                      <Text size="sm" color="dimmed">
                        {t("Service account email")}:{" "}
                        <Text span weight={500} color="gray.3">
                          {displayedServiceAccountEmail}
                        </Text>
                      </Text>
                    )}
                  </Group>
                  <Textarea
                    label={t("Service account JSON")}
                    description={
                      formValues.credentialsConfigured
                        ? t(
                            "Credentials are saved on the server. The field stays blank for security — paste new JSON only to replace them."
                          )
                        : t(
                            "Paste the full JSON key file contents from Google Cloud."
                          )
                    }
                    placeholder={
                      formValues.credentialsConfigured
                        ? t("Credentials saved — paste here only to replace…")
                        : t("Paste JSON here…")
                    }
                    minRows={5}
                    value={formValues.serviceAccountJson}
                    onChange={(event) =>
                      updateField("serviceAccountJson", event.currentTarget.value)
                    }
                  />
                </Stack>
              </FormSectionCard>

              <FormSectionCard
                title="Actions & sync status"
                description="Test setup before enabling sync. Backfill copies existing server files; retry re-queues failed jobs."
              >
                <Stack spacing="md">
                  <Group>
                    <Button
                      variant="filled"
                      loading={actionLoading === "test"}
                      onClick={() =>
                        runAction("test", () => api.fileBackup.testConnection())
                      }
                    >
                      {t("Test connection")}
                    </Button>
                    <Button
                      variant="light"
                      loading={actionLoading === "backfill"}
                      onClick={() => {
                        if (!window.confirm(t("Enqueue backfill for all unsynced files?"))) {
                          return;
                        }
                        runAction("backfill", () => api.fileBackup.backfill());
                      }}
                    >
                      {t("Backfill existing files")}
                    </Button>
                    <Button
                      variant="light"
                      loading={actionLoading === "retry"}
                      onClick={() => runAction("retry", () => api.fileBackup.retryFailed())}
                    >
                      {t("Retry failed jobs")}
                    </Button>
                  </Group>
                  {connectionHelp && (
                    <Alert
                      color="red"
                      variant="light"
                      title={t(connectionHelp.title)}
                      withCloseButton
                      onClose={() => {
                        setConnectionHelp(null);
                        setConnectionHelpOpen(false);
                      }}
                    >
                      <Stack spacing="xs">
                        <Text size="sm">{t(connectionHelp.summary)}</Text>
                        <Button
                          variant="light"
                          color="red"
                          size="xs"
                          onClick={() => setConnectionHelpOpen(true)}
                        >
                          {t("View step-by-step fix")}
                        </Button>
                      </Stack>
                    </Alert>
                  )}
                  {jobStatus && (
                    <Group spacing="xl">
                      <Stack spacing={0}>
                        <Text size="xs" color="dimmed">{t("Pending jobs")}</Text>
                        <Text size="lg" weight={600}>{jobStatus.pending}</Text>
                      </Stack>
                      <Stack spacing={0}>
                        <Text size="xs" color="dimmed">{t("Completed jobs")}</Text>
                        <Text size="lg" weight={600}>{jobStatus.synced}</Text>
                      </Stack>
                      <Stack spacing={0}>
                        <Text size="xs" color="dimmed">{t("Failed jobs")}</Text>
                        <Text size="lg" weight={600} color={jobStatus.failed > 0 ? "red" : undefined}>
                          {jobStatus.failed}
                        </Text>
                      </Stack>
                      <Stack spacing={0}>
                        <Text size="xs" color="dimmed">{t("Files mirrored")}</Text>
                        <Text size="lg" weight={600}>{jobStatus.syncRecords}</Text>
                      </Stack>
                      {statusLoading && (
                        <Text size="xs" color="dimmed">{t("Refreshing…")}</Text>
                      )}
                    </Group>
                  )}
                </Stack>
              </FormSectionCard>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="pathTemplates">
            <FormSectionCard>
              <Stack spacing="lg">
                <Stack spacing="xs">
                  <Title order={5}>{t("Path templates")}</Title>
                  <Text size="sm" color="dimmed">
                    {t(
                      "Choose which file types are backed up to Google Drive and how their folders are organized. Turn off types you do not need — for example, enable only Debit Note if the client only wants debit note documents mirrored."
                    )}
                  </Text>
                </Stack>
                {ENTITY_BACKUP_TYPE_META.map((meta) => {
                  const enabled =
                    formValues.entityTypeEnabled?.[meta.key] !== false;
                  return (
                    <Stack
                      key={meta.key}
                      spacing="sm"
                      p="md"
                      style={{
                        border: "1px solid var(--mantine-color-dark-4)",
                        borderRadius: 8,
                      }}
                    >
                      <Switch
                        label={t("Backup this file type")}
                        checked={enabled}
                        onChange={(event) =>
                          updateField(
                            `entityTypeEnabled.${meta.key}`,
                            event.currentTarget.checked
                          )
                        }
                      />
                      <Title order={6}>{t(meta.label)}</Title>
                      <Text size="sm" color="dimmed">
                        {t(meta.description)}
                      </Text>
                      <TextInput
                        label={t("Folder path template")}
                        description={t(
                          "Placeholders: {year}, {yearMonth}, {month}, {day}, {code}, {entityId}, {uploadFolder}"
                        )}
                        value={formValues.pathTemplates?.[meta.key] || ""}
                        disabled={!enabled}
                        onChange={(event) =>
                          updateField(
                            `pathTemplates.${meta.key}`,
                            event.currentTarget.value
                          )
                        }
                      />
                    </Stack>
                  );
                })}
              </Stack>
            </FormSectionCard>
          </Tabs.Panel>

          <Tabs.Panel value="advanced">
            <FormSectionCard>
              <Stack spacing="sm">
                <Title order={5}>{t("Advanced settings")}</Title>
                <Text size="sm" color="dimmed">
                  {t(
                    "Control how the background worker processes backup jobs. Increase batch size for faster catch-up; increase max retries for flaky network conditions."
                  )}
                </Text>
                <NumberInput
                  label={t("Max retries")}
                  min={1}
                  value={formValues.worker?.maxRetries ?? DEFAULT_WORKER.maxRetries}
                  onChange={(value) => updateField("worker.maxRetries", value)}
                />
                <NumberInput
                  label={t("Batch size")}
                  min={1}
                  value={formValues.worker?.batchSize ?? DEFAULT_WORKER.batchSize}
                  onChange={(value) => updateField("worker.batchSize", value)}
                />
              </Stack>
            </FormSectionCard>
          </Tabs.Panel>

          <Tabs.Panel value="setupGuide">
            <FileBackupSetupGuideTab
              serviceAccountEmail={displayedServiceAccountEmail}
            />
          </Tabs.Panel>
        </Tabs>
      )}

      <FileBackupErrorHelpModal
        opened={connectionHelpOpen}
        onClose={() => setConnectionHelpOpen(false)}
        presentation={connectionHelp}
      />
    </Page>
  );
};

export default FileBackupSyncSetting;
